<template>
    <div v-if="paymentMethods" class="row">
        <div
            class="col-12 d-flex flex-column mb-4"
            :class="isPaymentMethodUpdate ? '' : 'col-lg-7'"
        >
            <div class="card animated fadeInUp faster">
                <div class="card-header">
                    <div class="row align-items-center">
                        <div class="col-auto"><i class="fal fa-credit-card text-primary fa-2x"></i></div>
                        <div class="col pl-0">
                            <h5 class="mb-0 font-weight-normal">{{ messageHeader }}</h5>
                        </div>
                    </div>
                </div>
                <div class="card-body pb-3">
                    <div class="row justify-content-center no-gutters">
                        <div class="col-4 mb-2 col-md-auto text-center px-1" v-if="showSelectedMethod">
                            <img class="border border-2 rounded img-fluid payment-method-item border-primary" :src="selectedMethod.image" :title="selectedMethod.name" :alt="selectedMethod.name" :data-method="selectedMethod.ident" @click.prevent="selectPaymentMethod(selectedMethod.ident)" />
                        </div>
                        <div class="col-4 mb-3 col-md-auto text-center px-1" v-for="method in slicedPaymentMethods" :key="method.ident">
                            <img class="border border-2 rounded img-fluid payment-method-item" :class="{ 'border-primary': selectedMethod && method.ident === selectedMethod.ident }" :src="method.image" :title="method.name" :alt="method.name" :data-method="method.ident" @click.prevent="selectPaymentMethod(method.ident)" />
                        </div>
                    </div>
                    <div class="text-center mt-1">
                        <button type="button" v-b-modal="'payment-method-search'" class="btn btn-link btn-sm font-weight-bold" v-if="selectedMethod === null || selectedMethod.ident !== 'free'">
                            <i class="fal mr-2" :class="{ 'fa-plus': !paymentMethodLoading, 'fa-spin fa-spinner': paymentMethodLoading }"></i>
                            {{ $t("payment_method.show_more") }}
                        </button>
                        <div v-else>{{ $t("payment_method.no_payment_required") }}</div>
                    </div>
                </div>
                <Fields/>

                <Upselling v-if="showUpselling"/>
                <RoundUp v-if="showRoundUp"/>

                <div class="card-footer">
                    <div class="row align-items-center">
                        <div class="col-md-6 col-lg-6 mb-4 mb-md-0 mr-md-0">
                            <a @click="returnToPrevious()" href="javascript:void(0);" class="btn btn-text d-block d-md-inline-block px-0">
                                <i class="fas fa-angle-left mr-3"></i>
                                {{ messageReturn }}
                            </a>
                        </div>
                        <div class="col-md-6 col-lg-6 text-md-right">
                            <button type="button" class="btn btn-primary btn-block" @click="onSubmit" :disabled="checkoutButtonDisabled">
                                <template v-if="selectedMethod && selectedMethod.externalRedirect">{{ $t('payment_method.buttons.continue') }}</template>
                                <template v-else>{{ messageComplete }}</template>
                            </button>
                        </div>
                    </div>
                </div>
                <PaymentMethodSearch />
            </div>
        </div>
        <div v-if="!isPaymentMethodUpdate" class="d-flex flex-column col-12">
            <BasketList />
            <CreatorCode v-if="showCreatorCode" />
            <Coupon v-if="showCoupon"/>
        </div>
    </div>
    <div v-else class="text-center">
        <i class="fal fa-spinner fa-spin fa-2x"></i>
    </div>
</template>

<script>

    import { mapState, mapActions, mapMutations } from "vuex";
    import BasketList from "./BasketList";
    import Coupon from "./Coupon";
    import CreatorCode from "./CreatorCode";
    import PaymentMethodSearch from "./PaymentMethodSearch";
    import Fields from "./Fields/Fields";
    import RoundUp from "./RoundUp";
    import Upselling from "./Upselling";

    export default {
        name: "PaymentMethods",
        data() {
            return {
                paymentMethods: null,
                loading: false
            }
        },
        mounted() {
            this.fetchPaymentMethods();
            this.$root.$on('GooglePayMethodSelected', () => {
               this.onSubmit();
            });

            this.$root.$on('ApplePayMethodSelected', () => {
                this.onSubmit();
            });
        },
        computed: {
            ...mapState({
                selectedMethod: state => state.payment.selectedMethod,
                paymentMethodData: state => state.payment.paymentMethodData,
                paymentMethodLoading: state => state.payment.paymentMethodLoading,
                isPaymentMethodUpdate: state => state.basket.isPaymentMethodUpdate,
                store: state => state.app.store,
                recurringPayment: state => state.basket.recurring,
                balance: state => state.basket.price.balance,
                roundUp: state => state.roundUp,
            }),
            slicedPaymentMethods () {
                return this.paymentMethods.slice(0, this.showSelectedMethod ? 4 : 5);
            },
            checkoutButtonDisabled () {
                return this.paymentMethodLoading || this.loading || !this.paymentMethods;
            },
            showSelectedMethod () {
                if (!this.selectedMethod) {
                    return false;
                }

                if (this.selectedMethod.ident === 'paypal') {
                    this.$swal({
                        type: 'error',
                        text: 'Payment method updates are not available with this method'
                    });

                    return false;
                }

                for (let method in this.paymentMethods) {
                    if (this.paymentMethods[method].ident === this.selectedMethod.ident) {
                        return false;
                    }
                }
                return true;
            },
            messageComplete() {
                if (this.isPaymentMethodUpdate) {
                    return this.$t('payment_method_update.buttons.update_payment_method');
                }

                return this.recurringPayment
                    ? this.$t('payment_method.buttons.setup_subscription')
                    : this.$t('payment_method.buttons.complete_order');
            },
            messageHeader() {
              if (this.isPaymentMethodUpdate) {
                  return this.$t("payment_method_update.header")
              }

              return this.$t("payment_method.header");
            },
            messageReturn() {
                if (this.isPaymentMethodUpdate) {
                    return this.$t("payment_method_update.buttons.cancel")
                }

                return this.$t("payment_method.buttons.back_to_details");
            },
            showUpselling() {
                return !this.isPaymentMethodUpdate;
            },
            showCreatorCode() {
                return !this.isPaymentMethodUpdate && this.store.hasCreatorCodes;
            },
            showCoupon() {
                return !this.isPaymentMethodUpdate && !this.store.isStandalone
            },
            showRoundUp() {
                return !this.isPaymentMethodUpdate && this.roundUp && this.roundUp.amount > 0 && !this.recurringPayment
            }
        },
        methods: {
            returnToPrevious() {
                if (this.isPaymentMethodUpdate) {
                    window.location.href = this.$store.state.basket.returnUrl;
                } else {
                    this.previousStep();
                }
            },
            onSubmit() {
                if (!this.selectedMethod) {
                    return this.$swal({
                        type: 'error',
                        text: this.$t('payment_method.messages.payment_method_not_selected')
                    });
                }

                let error = this.paymentMethodData.data.error
                    ? this.paymentMethodData.data.error
                    : (this.isPaymentMethodUpdate ? this.$t('payment_method_update.messages.missing_fields') : this.$t('payment_method.messages.missing_fields'));

                if (!this.paymentMethodData.valid) {
                    return this.$swal({
                        type: 'error',
                        text: error
                    });
                }

                return this.isPaymentMethodUpdate
                    ? this.onUpdatePaymentMethodSubmit()
                    : this.onCheckoutSubmit();
            },
            onCheckoutSubmit() {
                this.loading = true;

                let nsureSdkLoaded = window.nsureSdkLoaded || (() => false);
                if (!nsureSdkLoaded) {
                    flare.reportMessage('nSure.ai SDK has not loaded', this.$store.state.payment);
                }

                this.checkout().catch(e => {
                    if (e.response.status === 504) {
                        this.$swal({
                            type: "error",
                            text: "An error occurred, please try again. If this issue persists please email checkout-support@tebex.io so that we can assist you further.",
                            confirmButtonText: "Try Again",
                            showLoaderOnConfirm: true,
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                            loaderHtml: '<i class="fal fa-spin fa-spinner"></i>',
                            preConfirm: async () => {
                                setTimeout(() => {
                                    // Wait 2s then reload
                                    window.location.reload();
                                }, 2000)

                                return new Promise(() => {
                                });
                            }
                        });

                        return;
                    }

                    this.$swal({ type: "error", text: e.response.data.message });
                }).finally(() => {
                    this.loading = false;
                });
            },
            onUpdatePaymentMethodSubmit() {
                this.loading = true;

                this.updatePaymentMethod().catch(e => {
                    if (e.response.status === 504) {
                        this.$swal({
                            type: "error",
                            text: "An error occurred, please try again. If this issue persists please email checkout-support@tebex.io so that we can assist you further.",
                            confirmButtonText: "Try Again",
                            showLoaderOnConfirm: true,
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                            loaderHtml: '<i class="fal fa-spin fa-spinner"></i>',
                            preConfirm: async () => {
                                setTimeout(() => {
                                    // Wait 2s then reload
                                    window.location.reload();
                                }, 2000)

                                return new Promise(() => {
                                });
                            }
                        });

                        return;
                    }

                    this.$swal({ type: "error", text: e.response.data.message });
                }).finally(() => {
                    this.loading = false;
                });
            },
            fetchPaymentMethods() {
                axios.get(tebex.application.basketApi + "/payment/method", {
                    params: {
                        country: this.$store.state.customer.country
                    }
                }).then(response => {
                    this.paymentMethods = response.data.data;
                    if (this.paymentMethods.length > 0) {
                        this.selectPaymentMethod(this.paymentMethods[0].ident);
                    }
                });
            },
            ...mapActions(['selectPaymentMethod', 'checkout', 'updatePaymentMethod']),
            ...mapMutations(['previousStep'])
        },
        watch: {
            balance () {
                this.fetchPaymentMethods();
            }
        },
        components: {
            Fields,
            PaymentMethodSearch,
            Coupon,
            CreatorCode,
            BasketList,
            RoundUp,
            Upselling
        }
    }

</script>

<style scoped lang="scss">
    .payment-method-item {
        padding: 10%;

        &:hover {
            border: 2px solid #105fbd !important;
        }
    }
</style>
